import { Divider, Stack, Typography, useTheme } from "@mui/material";
import {
  CircleQuestionMarkIcon,
  CircularLoader,
  CopyIcon,
  Input,
  SelectField,
  TextArea,
} from "components";
import {
  useAddNCentralConfig,
  useGetAllBoards,
  useGetIntegrationNotificationSettingsById,
  useGetMachineToken,
  useGetNcentralConfig,
  useGlobalStore,
  useUpdateNCentralConfig,
  useUpsertIntegrationNotificationSettings,
} from "controller";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import toast from "react-hot-toast";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { addMixpanelEvent } from "utilz/helpers";
import { ToggleButton } from "components/toggle";
import { INTEGRATION_SOURCE_IDS } from "../../integrationList";

const schema = yup.object({
  jwt: yup.string().required("JWT is required"),
  url: yup.string().url("Invalid URL").required("URL is required"),
  board: yup.string(),
});

export const NcentralStep1 = forwardRef(
  ({ onStepChange }: { onStepChange: (step: number) => void }, ref) => {
    const theme = useTheme();
    const mspid = useGlobalStore((state) => state.mspId);
    const { data: machineToken } = useGetMachineToken();
    const { data: boards } = useGetAllBoards({ serviceType: "ticket_board" });
    const { data: config } = useGetNcentralConfig({ enabled: true });
    const { mutateAsync: addConfig } = useAddNCentralConfig();
    const { mutateAsync: updateConfig } = useUpdateNCentralConfig();
    const { data: ncentralNotificationSettings } =
      useGetIntegrationNotificationSettingsById(
        INTEGRATION_SOURCE_IDS.NCENTRAL,
      );
    const {
      mutateAsync: upsertIntegrationNotificationSettings,
      isLoading: isUpsertIntegrationNotificationSettingsLoading,
    } = useUpsertIntegrationNotificationSettings();

    const [isNcentralNotificationsEnabled, setIsNcentralNotificationsEnabled] =
      useState(ncentralNotificationSettings?.is_enabled ?? true);
    useEffect(() => {
      setIsNcentralNotificationsEnabled(
        ncentralNotificationSettings?.is_enabled ?? true,
      );
    }, [ncentralNotificationSettings]);

    const onToggle = async () => {
      const initialSetting = ncentralNotificationSettings?.is_enabled ?? true;
      try {
        setIsNcentralNotificationsEnabled((prev: boolean) => !prev);
        await upsertIntegrationNotificationSettings({
          source_id: INTEGRATION_SOURCE_IDS.NCENTRAL,
          is_enabled: !initialSetting,
        });
      } catch (err: any) {
        setIsNcentralNotificationsEnabled(initialSetting);
        toast.error("Failed to update notification settings");
      }
    };

    const copyValue = (value: string) => {
      navigator.clipboard.writeText(value);

      toast.success("Copied to clipboard");
    };

    const {
      control,
      handleSubmit,
      getValues,
      watch,
      setValue,
      reset,
      formState: { errors },
    } = useForm({
      resolver: yupResolver(schema),
      defaultValues: {
        jwt: "",
        url: "",
        board: "",
      },
    });

    useEffect(() => {
      if (config) {
        console.log(config);
        reset({
          jwt: config?.web_token || "",
          url: config?.base_url || "",
          board: config?.board || "",
        });
      }
    }, [config]);

    const onSubmit = handleSubmit(
      async (data) => {
        try {
          // removes extra path from copied url

          const url = new URL(data.url);
          if (config === null) {
            await addConfig({
              webToken: data.jwt,
              baseUrl: `https://${url.hostname}`,
              board: data.board,
            });
            addMixpanelEvent({
              event: "ncentral_connection_success",
            });
          } else {
            await updateConfig({
              webToken: data.jwt,
              baseUrl: `https://${url.hostname}`,
              board: data.board,
            });
          }
          onStepChange(2);
        } catch (error: any) {
          toast.error(error?.message || "Unable to add N-central config");
        }

        // return data;
      },
      async (error) => {
        return error;
      },
    );

    useImperativeHandle(ref, () => {
      return {
        onSubmit,
      };
    });
    return (
      <Stack
        sx={{
          width: "100%",
          height: "100%",
          flexDirection: "row",
          overflowY: "auto",
          alignItems: "flex-start",
        }}
        divider={
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              height: "auto",
              alignSelf: "stretch",
            }}
          />
        }
      >
        <Stack
          sx={{
            flex: 1,
            gap: "16px",
            py: "24px",
            px: "32px",
          }}
        >
          <Typography variant="h6">API Ticket Generation</Typography>
          <Typography variant="body-small" color={theme.palette.lightBg.low}>
            Go to N-able’s PSA integration configurations page, select the
            option ‘Custom PSA’ as your PSA solution.
          </Typography>
          <Typography variant="body-small" color={theme.palette.lightBg.low}>
            Copy the credentials below and paste it on N-able N-central
          </Typography>

          <Stack sx={{ gap: "16px" }}>
            <Input
              name="endpoint"
              label="Base Endpoint URL"
              value="https://integration-api.deskday.ai"
              disabled={true}
              InputProps={{
                endAdornment: (
                  <CopyIcon
                    fontSize="small"
                    sx={{
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      copyValue("https://integration-api.deskday.ai");
                    }}
                  />
                ),
              }}
            ></Input>
            <Input
              name="ticketEndpoint"
              label="Ticket Endpoint"
              value="/rmm/ncentral/alert-webhook"
              disabled={true}
              InputProps={{
                endAdornment: (
                  <CopyIcon
                    fontSize="small"
                    sx={{
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      copyValue("/rmm/ncentral/alert-webhook");
                    }}
                  />
                ),
              }}
            ></Input>
            <Input
              name="username"
              label="Username"
              value={mspid}
              disabled={true}
              InputProps={{
                endAdornment: (
                  <CopyIcon
                    fontSize="small"
                    sx={{
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      copyValue(`${mspid}`);
                    }}
                  />
                ),
              }}
            ></Input>
            <Input
              name="password"
              label="Password"
              type="password"
              disabled={true}
              value={machineToken?.machine_token || " "}
              InputProps={{
                endAdornment: (
                  <CopyIcon
                    fontSize="small"
                    sx={{
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      copyValue(machineToken.machine_token);
                    }}
                  />
                ),
              }}
            ></Input>
          </Stack>
          <Typography variant="h6">Email Recipient</Typography>
          <Input
            name="email"
            label="Recipient Email"
            disabled={true}
            value={`Supportdesk@${mspid}.deskday.ai`}
            InputProps={{
              endAdornment: (
                <CopyIcon
                  cursor={"pointer"}
                  fontSize="small"
                  onClick={() => {
                    copyValue(`Supportdesk@${mspid}.deskday.ai`);
                  }}
                />
              ),
            }}
          />
        </Stack>
        <Stack
          sx={{
            flex: 1,
            // maxWidth: "446px",
            gap: "16px",
            pt: "24px",
            pb: "64px",
            px: "32px",
          }}
        >
          <Typography variant="h6">Configure Advanced Integration</Typography>
          <Typography variant="body-small" color={theme.palette.lightBg.low}>
            Copy the web token provided and paste it below
          </Typography>
          <TextArea
            control={control}
            name={"jwt"}
            placeholder="Place web token here..."
            minRows={5}
            error={errors.jwt ? true : false}
            errorMessage={errors.jwt?.message}
            style={{
              backgroundColor: theme.palette.neutral["005"],
              width: "100%",
              borderRadius: "4px",
              padding: "12px",
              height: "95%",
              border: "none",

              ...theme.typography["body-small"],
              color: theme.palette.lightBg.medium,
            }}
          ></TextArea>

          <Typography variant="body-small" color={theme.palette.lightBg.low}>
            Copy the URL provided by N-central and paste it below
          </Typography>
          <Input
            name="url"
            label="URL"
            inputProps={{
              endAdornment: <CircleQuestionMarkIcon fontSize="small" />,
            }}
            placeholder="https://xxxxxx.n-able.com"
            control={control}
            error={errors.url ? true : false}
            errorMessage={errors.url?.message}
          />
          <Typography variant="body-small" color={theme.palette.lightBg.low}>
            Choose your service desk board
          </Typography>
          <SelectField
            name="board"
            label="Board"
            control={control}
            options={boards?.result}
            valueMappingKey="ticket_board_id"
            labelMappingKey="ticket_board_name"
            isSearchable
            error={errors.board ? true : false}
            errorMessage={errors.board?.message}
            searchFieldProps={{
              placeholder: "Search board",
            }}
          />
          <Typography variant="body-small" color={theme.palette.lightBg.low}>
            Alert notifications
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            gap="8px"
            p="16px"
            border={`1px solid ${theme.palette.neutral["015"]}`}
            borderRadius="4px"
          >
            <ToggleButton
              isChecked={isNcentralNotificationsEnabled}
              onChange={onToggle}
              isDisabled={isUpsertIntegrationNotificationSettingsLoading}
            />
            <Typography variant="button-label">
              Enable to get notifications for alerts
            </Typography>
            {isUpsertIntegrationNotificationSettingsLoading && (
              <CircularLoader
                size="16px"
                sx={{
                  ml: "auto",
                }}
              />
            )}
          </Stack>
        </Stack>
      </Stack>
    );
  },
);
